import { useState, useMemo } from 'react'
import type { Post, PostPoll } from '@/types/posts'
import { usePollVote } from '@/hooks/usePollVote'
import { useAuth } from '@/hooks/useAuth'

interface PollCardProps {
    post: Post
    poll: PostPoll
}

/**
 * Renders a Mastodon poll inline within a PostCard.
 *
 * States handled:
 * - Unauthenticated: read-only with percentages
 * - Expired: read-only with results
 * - hide_totals (active, not voted): options visible but no counts
 * - Authenticated, not voted: clickable options + Vote button
 * - Authenticated, voted: results with own selections highlighted
 */
export function PollCard({ post, poll }: PollCardProps) {
    const { mastodonSession } = useAuth()
    const { vote, isVoting } = usePollVote()
    const [selected, setSelected] = useState<number[]>([])

    // Snapshot of "now" for consistent time calculations within this render
    const now = useMemo(() => new Date(), [])

    const isAuthenticated = !!mastodonSession
    const canVote = isAuthenticated && !poll.voted && !poll.expired && post.protocol !== 'pixelfed'
    // hide_totals: votes_count is null for all options while poll is active
    const totalsHidden = !poll.expired && poll.options.some(o => o.votesCount === null)
    // Genuine boolean-OR: show results if ANY condition holds. ?? would short-circuit on a
    // `false` poll.voted and never check the rest.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const showResults = poll.voted || poll.expired || !canVote

    const handleToggle = (index: number) => {
        if (!canVote || isVoting) return
        if (poll.multiple) {
            setSelected(prev =>
                prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
            )
        } else {
            setSelected([index])
        }
    }

    const handleVote = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (selected.length === 0 || isVoting) return
        vote(post, selected)
        setSelected([])
    }

    const getPercentage = (optionVotes: number | null): number => {
        if (optionVotes === null || poll.totalVotes === 0) return 0
        return Math.round((optionVotes / poll.totalVotes) * 100)
    }

    const maxVotes = Math.max(...poll.options.map(o => o.votesCount ?? 0))

    const getExpiryLabel = (): string | null => {
        if (poll.expired) {
            if (!poll.expiresAt) return 'Poll ended'
            const ended = new Date(poll.expiresAt)
            const diffMs = now.getTime() - ended.getTime()
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
            if (diffDays > 0) return `Ended ${diffDays}d ago`
            if (diffHours > 0) return `Ended ${diffHours}h ago`
            return 'Just ended'
        }
        if (!poll.expiresAt) return null
        const expires = new Date(poll.expiresAt)
        const diffMs = expires.getTime() - now.getTime()
        if (diffMs <= 0) return 'Ending soon'
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffMin = Math.floor(diffMs / (1000 * 60))
        if (diffDays > 0) return `${diffDays}d left`
        if (diffHours > 0) return `${diffHours}h left`
        if (diffMin > 0) return `${diffMin}m left`
        return 'Ending soon'
    }
    const expiryLabel = getExpiryLabel()

    return (
        <div
            className="mt-2 border border-border-light dark:border-border-dark rounded-lg p-3 space-y-2"
            onClick={e => e.stopPropagation()}
        >
            {/* Options */}
            <div className="space-y-1.5">
                {poll.options.map((option, index) => {
                    const pct = showResults && !totalsHidden ? getPercentage(option.votesCount) : 0
                    const isOwnVote = poll.ownVotes?.includes(index) ?? false
                    const isSelectedPending = selected.includes(index)
                    const isWinner =
                        showResults &&
                        !totalsHidden &&
                        poll.totalVotes > 0 &&
                        option.votesCount !== null &&
                        option.votesCount === maxVotes

                    return (
                        <div key={index}>
                            {canVote ? (
                                /* Voting mode: clickable option */
                                <button
                                    onClick={() => handleToggle(index)}
                                    disabled={isVoting}
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                                        isSelectedPending
                                            ? 'border-primary bg-primary/10 text-text-primary'
                                            : 'border-border-light dark:border-border-dark text-text-primary hover:bg-surface-light dark:hover:bg-surface-dark'
                                    } ${isVoting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <span
                                        className={`flex-shrink-0 w-4 h-4 rounded-${poll.multiple ? 'sm' : 'full'} border-2 flex items-center justify-center ${
                                            isSelectedPending
                                                ? 'border-primary bg-primary'
                                                : 'border-border-light dark:border-border-dark'
                                        }`}
                                    >
                                        {isSelectedPending && (
                                            <span className="w-2 h-2 rounded-full bg-white" />
                                        )}
                                    </span>
                                    <span className="flex-1">{option.title}</span>
                                </button>
                            ) : (
                                /* Results mode: progress bar */
                                <div
                                    className={`relative overflow-hidden rounded-md px-3 py-2 ${
                                        isOwnVote
                                            ? 'bg-primary/10 dark:bg-primary/20'
                                            : 'bg-surface-light dark:bg-surface-dark'
                                    }`}
                                >
                                    {/* Progress fill */}
                                    {!totalsHidden && (
                                        <div
                                            className={`absolute inset-y-0 left-0 rounded-md transition-all duration-500 ${
                                                isWinner
                                                    ? 'bg-primary/20 dark:bg-primary/30'
                                                    : 'bg-border-light dark:bg-border-dark'
                                            }`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    )}

                                    {/* Text row */}
                                    <div className="relative flex items-center gap-2 text-sm">
                                        {isOwnVote && (
                                            <span className="text-primary flex-shrink-0">✓</span>
                                        )}
                                        <span
                                            className={`flex-1 font-${isWinner && !totalsHidden ? 'semibold' : 'normal'} text-text-primary`}
                                        >
                                            {option.title}
                                        </span>
                                        {!totalsHidden && (
                                            <span className="flex-shrink-0 text-text-secondary text-xs">
                                                {pct}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Vote button (only when not yet voted and poll is active) */}
            {canVote && (
                <button
                    onClick={handleVote}
                    disabled={selected.length === 0 || isVoting}
                    className="mt-1 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                >
                    {isVoting ? 'Voting…' : 'Vote'}
                </button>
            )}

            {/* Footer: vote count + expiry + hints */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-secondary">
                <span>{poll.totalVotes === 1 ? '1 vote' : `${poll.totalVotes} votes`}</span>
                {expiryLabel && (
                    <>
                        <span>·</span>
                        <span>{expiryLabel}</span>
                    </>
                )}
                {totalsHidden && !poll.expired && (
                    <>
                        <span>·</span>
                        <span>Results hidden until poll ends</span>
                    </>
                )}
                {poll.voted && !poll.expired && (
                    <>
                        <span>·</span>
                        <span>Votes can&apos;t be changed</span>
                    </>
                )}
            </div>
        </div>
    )
}
